import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, BookOpen, Home, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { products } from "@/data/products";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    // Keep search engines from indexing unknown URLs and give the tab a clear title.
    const prevTitle = document.title;
    document.title = "Page not found | Bane Tech";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, follow";
    document.head.appendChild(robots);
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    canonical?.remove();
    return () => {
      document.title = prevTitle;
      robots.remove();
      if (canonical) document.head.appendChild(canonical);
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Navbar />
      <main className="flex-1">
        <section className="pt-28 pb-12 md:pt-36 bg-hero-network">
          <div className="container max-w-4xl text-center">
            <p className="font-mono text-xs uppercase tracking-widest text-primary">Error 404</p>
            <h1 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight">Page not found</h1>
            <p className="mt-4 text-lg text-muted-foreground">The page you are looking for does not exist or has moved.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button variant="hero" size="lg" asChild>
                <a href="/"><Home className="h-4 w-4" />Back to Home</a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="/resources/"><BookOpen className="h-4 w-4" />Resources</a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="/#contact"><Mail className="h-4 w-4" />Contact us</a>
              </Button>
            </div>
          </div>
        </section>
        <section className="container max-w-5xl pb-20">
          <h2 className="font-display text-xl font-semibold mb-5">Explore our products</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <a key={p.slug} href={`/products/${p.slug}/`} className="group flex items-center gap-4 rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
                <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${p.accent}`}>
                  <img src={p.icon} alt="" width={26} height={26} className="h-6 w-6" />
                </span>
                <span className="flex-1">
                  <span className="block font-medium group-hover:text-primary transition-colors">{p.name}</span>
                  <span className="block text-xs text-muted-foreground">{p.tagline}</span>
                </span>
                <ArrowRight className="h-4 w-4 text-primary" />
              </a>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
